// src/PaymentRace.tsx
//
// Přihláška na závod (/payment/:raceId): souhrn závodu, formulář přihlášky
// (ráže, kompenzátor, preference, nováček, doplňující otázky pořadatele),
// stav registrace s platebními pokyny (sdílená PaymentInstructions) a
// odhlášení s potvrzením dopadů. Titulek a Zpět (/user) dodává Screen
// v App.tsx, nepřihlášené odchytí RequireAuth.
//
// Beze změny (docs/redesign-plan.md, krok 6): deterministické ID registrace
// {raceId}__{uid}, pole dokumentu registrace, číslo dokladu v userDocuments/{uid}
// jen pro rok závodu. Variabilní symbol, zpráva pro příjemce, SPAYD i e-mail
// jsou v utils/payments.ts, cena a kategorie v utils/pricing.ts.
//
// Obsazenost (bezpečnost a, 3. 10. 2026): cizí přihlášky už číst nejde –
// počet je ve veřejném race_stats/{raceId} a přihláška ho posouvá o +1 ve
// STEJNÉ transakci, která hlídá i kapacitu (utils/raceStats.ts; dřív se počet
// četl mimo transakci a souběžné přihlášky kapacitu překročily). Plný závod
// nabízí čekací listinu (utils/waitlist.ts – upozornění „Uvolnilo se místo"
// v aplikaci a od 5. 10. 2026 i e-mail ze serveru, cloud-functions/payments/
// waitlist.py).
//
// Startovné kontrolují pravidla proti aktuálnímu závodu a profilu. Odmítnutá
// přihláška (trvalé permission-denied, nebo nával i po opakováních v raceStats)
// proto načte závod a profil znovu – změněnou cenu (mezitím zaplacené
// členství, nový ceník) formulář ukáže a příště odešle. Nával bez změny ceny
// ukáže „Na závod se teď hlásí víc lidí najednou…" (RaceBusyError, i při
// odhlášení), ne „nemáte oprávnění".

import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
} from "firebase/firestore";
import type { User } from "firebase/auth";
import { db } from "./firebase";
import { useToast } from "./ui/Toast";
import { useConfirm } from "./ui/ConfirmDialog";
import {
  AsyncState,
  EmptyState,
  ErrorState,
  LoadingState,
  useAsync,
} from "./ui/AsyncState";
import { Section, SectionHeader } from "./ui/Section";
import { Field } from "./ui/Field";
import { ChoiceGroup, YesNoToggle, type ChoiceOption } from "./ui/Toggle";
import {
  ActionList,
  DetailList,
  DetailRow,
  InfoCard,
  type ActionListItem,
} from "./ui/Cards";
import { DisciplineBadge } from "./ui/DisciplineBadge";
import { PaymentInstructions } from "./ui/PaymentInstructions";
import { scrollIntoMain } from "./ui/AppShell";
import { useAuth } from "./utils/useAuth";
import { ensureVerifiedToken, useEmailVerification } from "./utils/emailVerification";
import { registrationClosesAtOf } from "./utils/registrationDeadline";
import { EmailVerificationNotice } from "./ui/EmailVerificationNotice";
import { useJuniorMaxAge } from "./utils/getUserCategory";
import { calculatePrice, getPriceCategory, toDateSafe } from "./utils/pricing";
import {
  buildPaymentQrData,
  buildRaceVariableSymbol,
  formatCzk,
  isRegistrationPaid,
  loadBankDetails,
  paymentDueDate,
  priceCategoryLabel,
  racePaymentMessage,
  sendRacePaymentEmail,
} from "./utils/payments";
import { firebaseErrorMessage } from "./utils/firebaseErrors";
import { reportError, reportWarning } from "./utils/reportError";
import { isValidSequenceNumber } from "./utils/memberNumber";
import { CONTACT_EMAIL } from "./utils/contact";
import { isIOS } from "./utils/platform";
import {
  RaceBusyError,
  RaceFullError,
  capacityOf,
  createRegistrationCounted,
  deleteRegistrationCounted,
  isRaceFull,
  loadRegisteredCount,
  type CreateRegistrationResult,
} from "./utils/raceStats";
import {
  joinWaitlist,
  leaveWaitlist,
  loadRaceWaitlist,
  waitlistId,
  waitlistPosition,
  type WaitlistEntry,
} from "./utils/waitlist";
import { startListPath } from "./utils/startList";
import { useAddToCalendar } from "./AddToCalendar";

// ─────────────────────────────────────────────────────────────
//  Typy
// ─────────────────────────────────────────────────────────────

interface ExtraQuestion {
  type: "yesno" | "text" | "document";
  label: string;
  required?: boolean;
}

interface Race {
  id: string;
  name: string;
  /** Firestore Timestamp nebo řetězec "YYYY-MM-DD". */
  date: any;
  /** Někde string "008", jinde number 8 – necháme any. */
  raceNumber: any;
  priceMember?: number;
  priceNonMember?: number;
  priceWoman?: number;
  priceJunior?: number;
  shootingRange?: string;
  mapUrl?: string;
  notes?: string;
  organizedByLKS?: boolean;
  rulesPdfUrl?: string;
  /** kulovnice / malorážka (starší závody pole nemají) */
  discipline?: string;
  extraQuestions: ExtraQuestion[];
  /** Uzávěrka přihlášek (Timestamp / string); bez ní start dne závodu. */
  registrationClosesAt?: any;
  /** Kapacita (kladné číslo); null = neomezeno – i prázdné a 0 (capacityOf). */
  maxCapacity: number | null;
}

/** Údaje střelce z users/{uid} potřebné pro přihlášku (normalizace beze změny). */
interface Profile {
  uid: string;
  /** Členské číslo tak, jak je uložené (už paddované) – část variabilního symbolu. */
  sequenceNumber: string;
  firstName: string;
  lastName: string;
  birthYear: number;
  /** VELKÝMI písmeny ("ŽENA"/"MUŽ") – tak ho čte utils/pricing. */
  gender: string;
  email: string;
  membership: { [year: string]: string };
  isNewbie: string;
  /** Systém volbu nováčka trvale zakázal. */
  newbieDisabled: boolean;
  weaponCaliber: string;
  triggerBrake: string;
  shootingWith: string;
  weaponWith: string;
}

/** Registrace uživatele na závod: id dokumentu + jeho data (startFee, paidAmount, odpovědi…). */
interface Registration {
  id: string;
  data: DocumentData;
}

interface DocumentPrefill {
  type: DocTypeCode;
  number: string;
}

interface PageData {
  /** null = závod neexistuje. */
  race: Race | null;
  /** null = dokument users/{uid} chybí. */
  profile: Profile | null;
  /** Všechny registrace uživatele na tento závod – zaplacená první, pak nejstarší. */
  registrations: Registration[];
  /**
   * Počet přihlášených (obsazenost z race_stats) – jen závod s kapacitou;
   * null = závod bez kapacity nebo čtení selhalo (v UI „…").
   */
  registrationCount: number | null;
  /**
   * Čekací listina závodu (i bez kapacity – správce ji mohl zrušit a střelec
   * se musí jít z listiny odebrat); null = nenačtená (přihlášený střelec,
   * chyba čtení).
   */
  waitlist: WaitlistEntry[] | null;
  /** Číslo dokladu k předvyplnění (jen pro rok závodu). */
  documentPrefill: DocumentPrefill | null;
}

// ─────────────────────────────────────────────────────────────
//  Typy dokladů totožnosti (otázka typu "document")
//  Jeden zdroj pravdy pro UI labely, krátký kód do exportu i normalizaci.
// ─────────────────────────────────────────────────────────────

type DocTypeCode = "OP" | "EUZP" | "CP";

const DOC_TYPES: {
  code: DocTypeCode;
  button: string;
  field: string;
  short: string;
  icon: string;
}[] = [
  {
    code: "OP",
    button: "Občanský průkaz",
    field: "Číslo občanského průkazu",
    short: "OP",
    icon: "bi-person-vcard",
  },
  {
    code: "EUZP",
    button: "EU zbrojní průkaz",
    field: "Číslo EU zbrojního průkazu",
    short: "EU ZP",
    icon: "bi-card-text",
  },
  {
    code: "CP",
    button: "Cestovní pas",
    field: "Číslo cestovního pasu",
    short: "CP",
    icon: "bi-passport",
  },
];

const DOC_TYPE_OPTIONS: ChoiceOption<DocTypeCode>[] = DOC_TYPES.map((d) => ({
  value: d.code,
  label: d.button,
  icon: d.icon,
}));

const normalizeDocType = (v: unknown): DocTypeCode =>
  v === "EUZP" || v === "CP" ? v : "OP";

const docTypeMeta = (code: DocTypeCode) =>
  DOC_TYPES.find((d) => d.code === code) || DOC_TYPES[0];

// ─────────────────────────────────────────────────────────────
//  Pomocné funkce
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────
//  ANTI-DUPLICITA: deterministické ID registrace
//  Jeden uživatel = jeden dokument na závod. Druhý zápis je
//  na úrovni Firestore fyzicky nemožný (stejná cesta dokumentu).
//  Formát: "{raceId}__{userId}"
// ─────────────────────────────────────────────────────────
const buildRegistrationId = (raceId: string, userId: string): string =>
  `${raceId}__${userId}`;

/** CZ datum + čas ("16.05.2026 18:00"). */
const formatCzDateTime = (d: Date): string =>
  d.toLocaleString("cs-CZ", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Rok závodu pro doklad v userDocuments (ukládá se i čte jen pro rok závodu).
 * Pro "YYYY-MM-DD" shodné s dřívějším new Date(race.date).getFullYear();
 * Timestamp navíc přečte správně.
 */
const raceYearOf = (race: Race): number => {
  const d = toDateSafe(race.date);
  return d ? d.getFullYear() : new Date(race.date).getFullYear();
};

/** Uložené "ano"/"ne" pro zobrazení. */
const yesNoLabel = (value: unknown): string => {
  if (value === "ano") return "Ano";
  if (value === "ne") return "Ne";
  return typeof value === "string" ? value : "";
};

/** Externí odkaz (Apple Maps) v novém okně bez přístupu k openeru. */
const openExternal = (url: string): void => {
  const win = window.open(url, "_blank");
  if (win) win.opener = null;
};

/**
 * Chyba ve formuláři: pole (nebo skupinu tlačítek) odscroluje pod fixní
 * hlavičku – scroll kontejner je .lks-main, ne okno – a inputu dá fokus.
 */
const focusInvalid = (id: string): void => {
  const el =
    document.getElementById(id) || document.getElementById(`${id}-label`);
  if (!el) return;
  const block = el.closest(".lks-field, .lks-yesno, .lks-choice");
  scrollIntoMain((block as HTMLElement | null) || (el as HTMLElement));
  if (/^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) {
    (el as HTMLElement).focus({ preventScroll: true });
  }
};

/**
 * Po odeslání přihlášky nahoru na stavový banner – scroll kontejner je
 * .lks-main (window.scrollTo nic nedělá). Starší WebView bez scrollTo
 * s options spadne na přímé nastavení scrollTop.
 */
const scrollMainToTop = (): void => {
  const main = document.querySelector(".lks-main") as HTMLElement | null;
  if (!main) return;
  try {
    main.scrollTo({ top: 0, behavior: "smooth" });
  } catch {
    main.scrollTop = 0;
  }
};

/** Odkaz na správce klubu do hlášek (chybějící členské číslo, vrácení platby). */
const ContactLink: React.FC = () => (
  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
);

/**
 * Cena závodu jako číslo: jeden starší závod má ceny uložené jako text
 * ("1000") – startovné přihlášky musí být číslo (pravidla ho porovnávají
 * s cenou kategorie). Prázdné / nečitelné = cena neuvedena (calculatePrice
 * ji bere jako 0).
 */
const toPrice = (raw: unknown): number | undefined => {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw === "string" && raw.trim() !== "") {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

const toRace = (id: string, data: DocumentData): Race => ({
  id,
  name: typeof data.name === "string" ? data.name : "",
  date: data.date,
  raceNumber: data.raceNumber,
  priceMember: toPrice(data.priceMember),
  priceNonMember: toPrice(data.priceNonMember),
  priceWoman: toPrice(data.priceWoman),
  priceJunior: toPrice(data.priceJunior),
  shootingRange: data.shootingRange,
  mapUrl: data.mapUrl,
  notes: data.notes,
  organizedByLKS: data.organizedByLKS,
  rulesPdfUrl: data.rulesPdfUrl,
  discipline: data.discipline,
  extraQuestions: Array.isArray(data.extraQuestions) ? data.extraQuestions : [],
  registrationClosesAt: data.registrationClosesAt,
  // 0 a prázdné = neomezeno (nápověda formuláře závodu; dřív 0 = „plno")
  maxCapacity: capacityOf(data),
});

/** Normalizace users dokumentu – stejná jako dřív (gender verzálkami, výchozí "ne"). */
const toProfile = (uid: string, data: DocumentData): Profile => ({
  uid,
  sequenceNumber: String(data.sequenceNumber ?? ""),
  firstName: data.firstName,
  lastName: data.lastName,
  birthYear: data.birthYear,
  gender: (data.gender || "").toUpperCase(),
  email: data.email,
  membership: data.membership || {},
  isNewbie: data.isNewbie || "ne",
  newbieDisabled: data.newbieDisabled === true,
  weaponCaliber: data.weaponCaliber || "",
  triggerBrake: data.triggerBrake || "ne",
  shootingWith: data.shootingWith || "",
  weaponWith: data.weaponWith || "",
});

/** Údaje profilu, ze kterých utils/pricing počítá kategorii a startovné. */
const priceUserOf = (profile: Profile) => ({
  birthYear: profile.birthYear,
  gender: profile.gender,
  membership: profile.membership,
});

const isPermissionDenied = (err: unknown): boolean =>
  typeof err === "object" &&
  err !== null &&
  (err as { code?: unknown }).code === "permission-denied";

/**
 * Kdyby historicky existovaly duplicity, platná je ta, na které je evidovaná
 * platba, jinak NEJSTARŠÍ (beze změny).
 */
const sortRegistrations = (regs: Registration[]): Registration[] =>
  [...regs].sort((a, b) => {
    const pa = Number(a.data.paidAmount) || 0;
    const pb = Number(b.data.paidAmount) || 0;
    if (pa !== pb) return pb - pa; // zaplacená první
    const ta = toDateSafe(a.data.registeredAt)?.getTime() ?? 0;
    const tb = toDateSafe(b.data.registeredAt)?.getTime() ?? 0;
    return ta - tb; // pak nejstarší
  });

/** Registrace uživatele na závod – všechny dokumenty (kvůli self-healingu duplicit). */
const fetchOwnRegistrations = async (
  raceId: string,
  uid: string
): Promise<Registration[]> => {
  const snap = await getDocs(
    query(
      collection(db, "registrations"),
      where("userId", "==", uid),
      where("raceId", "==", raceId)
    )
  );
  return sortRegistrations(snap.docs.map((d) => ({ id: d.id, data: d.data() })));
};

/**
 * Doklad k předvyplnění jen pro stejný kalendářní rok jako závod (GDPR –
 * data se nedrží napříč roky). Číslo se čte z PRIVÁTNÍ kolekce
 * userDocuments/{uid} (přístup jen vlastník + admin). Volitelná data:
 * chyba čtení stránku neshodí, jen se nepředvyplní.
 */
const loadDocumentPrefill = async (
  uid: string,
  raceYear: number
): Promise<DocumentPrefill | null> => {
  try {
    const snap = await getDoc(doc(db, "userDocuments", uid));
    if (!snap.exists()) return null;
    const d = snap.data();
    if (d?.number && d?.year === raceYear) {
      return { type: normalizeDocType(d.type), number: String(d.number) };
    }
    return null;
  } catch (err) {
    reportWarning("PaymentRace.loadDocumentPrefill", err, { uid });
    return null;
  }
};

/**
 * Obsazenost z veřejného race_stats. Volitelná data: chyba čtení stránku
 * neshodí – počet je neznámý (null) a o kapacitě rozhodne transakce přihlášky.
 */
const loadRegisteredCountSafe = async (raceId: string): Promise<number | null> => {
  try {
    return await loadRegisteredCount(raceId);
  } catch (err) {
    reportWarning("PaymentRace.loadRegisteredCount", err, { raceId });
    return null;
  }
};

/** Čekací listina závodu; chyba čtení stránku neshodí (null = nenačtená). */
const loadRaceWaitlistSafe = async (
  raceId: string
): Promise<WaitlistEntry[] | null> => {
  try {
    return await loadRaceWaitlist(raceId);
  } catch (err) {
    reportWarning("PaymentRace.loadRaceWaitlist", err, { raceId });
    return null;
  }
};

/**
 * Vše, co obrazovka potřebuje, jedním načtením (useAsync → jeden stav, jeden
 * retry). Závod, profil a vlastní přihlášky jsou nutné; obsazenost (jen závod
 * s kapacitou), čekací listina i předvyplnění dokladu jsou volitelné.
 */
const loadPage = async (raceId: string, uid: string): Promise<PageData> => {
  const [raceSnap, userSnap, registrations] = await Promise.all([
    getDoc(doc(db, "races", raceId)),
    getDoc(doc(db, "users", uid)),
    fetchOwnRegistrations(raceId, uid),
  ]);
  const race = raceSnap.exists() ? toRace(raceSnap.id, raceSnap.data()) : null;
  const profile = userSnap.exists() ? toProfile(uid, userSnap.data()) : null;
  const hasCapacity = !!race && race.maxCapacity !== null;
  // Přihlášený střelec čekací listinu nevidí (přihláška jeho záznam smazala,
  // odhlášení načte stránku znovu) – čte ji jen nepřihlášený, i u závodu bez
  // kapacity (odebrání z listiny po zrušení kapacity).
  const needsWaitlist = !!race && registrations.length === 0;
  const [documentPrefill, registrationCount, waitlist] = await Promise.all([
    race && profile ? loadDocumentPrefill(uid, raceYearOf(race)) : null,
    hasCapacity ? loadRegisteredCountSafe(raceId) : null,
    needsWaitlist ? loadRaceWaitlistSafe(raceId) : null,
  ]);
  return {
    race,
    profile,
    registrations,
    registrationCount,
    waitlist,
    documentPrefill,
  };
};

// ─────────────────────────────────────────────────────────────
//  Sekce „Závod" – souhrn před přihlášením i po něm
// ─────────────────────────────────────────────────────────────

interface RaceSummaryProps {
  race: Race;
  raceDateObj: Date | null;
  registrationClosesAt: Date | null;
  /** Kapacita (null = neomezeno – řádek Obsazenost se neukazuje). */
  capacity: number | null;
  /** Počet přihlášených; null = neznámý („…"). */
  registrationCount: number | null;
  isFull: boolean;
  isLksRace: boolean;
  /** Ceník načtený – do té doby se kategorie a cena neukazují (jinak by bliknuly špatně). */
  pricingReady: boolean;
  /** Chyba načtení ceníku (null = bez chyby) – řádky ukážou hlášku se „Zkusit znovu". */
  pricingError: unknown;
  onRetryPricing: () => void;
  categoryLabel: string;
  /** Živé startovné (calculatePrice) – platí před přihlášením. */
  price: number;
  /**
   * Startovné uložené v registraci – po přihlášení má přednost před živou
   * cenou (ta se mohla změnit, platí se to, co je v dokumentu).
   */
  registeredFee?: number;
  onNavigate: (to: string) => void;
}

const RaceSummarySection: React.FC<RaceSummaryProps> = ({
  race,
  raceDateObj,
  registrationClosesAt,
  capacity,
  registrationCount,
  isFull,
  isLksRace,
  pricingReady,
  pricingError,
  onRetryPricing,
  categoryLabel,
  price,
  registeredFee,
  onNavigate,
}) => {
  // „Přidat do kalendáře" – jen u závodu, který teprve bude
  const calendar = useAddToCalendar(race);
  const links: ActionListItem[] = [];
  if (race.rulesPdfUrl) {
    links.push({
      icon: "bi-file-earmark-text",
      label: "Propozice",
      to: `/pdfviewer/${race.id}`,
    });
  }
  if (race.mapUrl) {
    links.push({
      icon: "bi-geo-alt",
      label: "Mapa (Google)",
      to: `/mapviewer/${race.id}`,
    });
    if (isIOS()) {
      links.push({
        icon: "bi-apple",
        label: "Mapa (Apple)",
        onClick: () =>
          openExternal(
            `http://maps.apple.com/?q=${encodeURIComponent(
              race.shootingRange || race.name
            )}`
          ),
      });
    }
  }
  if (calendar.available) {
    links.push({
      icon: "bi-calendar-plus",
      label: "Přidat do kalendáře",
      onClick: calendar.open,
    });
  }

  // Hodnota závislá na ceníku: chyba s retry / načítání / hotovo.
  const pricingValue = (value: React.ReactNode): React.ReactNode =>
    pricingError != null ? (
      <ErrorState inline error={pricingError} onRetry={onRetryPricing} />
    ) : !pricingReady ? (
      <LoadingState inline text="Načítám…" />
    ) : (
      value
    );
  const feeLabel = (fee: number): string =>
    fee === 0 ? "Zdarma" : formatCzk(fee);

  return (
    <Section title="Závod" icon="bi-flag">
      <p className="fs-5 fw-semibold mb-2" style={{ lineHeight: 1.3 }}>
        {race.name}
        <DisciplineBadge
          race={race}
          size="md"
          className="ms-2"
          style={{ verticalAlign: "middle" }}
        />
      </p>

      {!isLksRace && (
        <div className="alert alert-warning py-2 mb-3" role="note">
          <i className="bi bi-exclamation-triangle me-2" aria-hidden="true" />
          Tento závod nepořádá LKS – startovné plaťte podle pokynů pořadatele.
        </div>
      )}

      {race.notes ? (
        <p className="small text-muted" style={{ whiteSpace: "pre-line" }}>
          {race.notes}
        </p>
      ) : null}

      <DetailList>
        <DetailRow
          label="Datum"
          value={
            raceDateObj ? raceDateObj.toLocaleDateString("cs-CZ") : String(race.date)
          }
        />
        <DetailRow label="Střelnice" value={race.shootingRange || "—"} />
        {registrationClosesAt ? (
          <DetailRow
            label="Uzávěrka přihlášek"
            value={formatCzDateTime(registrationClosesAt)}
          />
        ) : null}
        {capacity !== null ? (
          <DetailRow
            label="Obsazenost"
            value={
              <>
                {registrationCount ?? "…"} / {capacity}
                {isFull && <span className="badge bg-danger ms-2">Plno</span>}
              </>
            }
          />
        ) : null}
        {isLksRace ? (
          <DetailRow
            label="Vaše kategorie"
            value={pricingValue(
              registeredFee !== undefined ? (
                categoryLabel
              ) : (
                <>
                  {categoryLabel}
                  {/* Kategorie podle roku narození a pohlaví v profilu – ty
                      vlastník nezmění (rozhodnutí majitele 12. 10. 2026). */}
                  <span className="d-block small text-muted">
                    Pohlaví a rok narození může změnit jen správce.
                  </span>
                </>
              )
            )}
          />
        ) : null}
        <DetailRow
          label="Startovné"
          value={
            !isLksRace
              ? "Podle pokynů pořadatele"
              : registeredFee !== undefined
              ? feeLabel(registeredFee)
              : pricingValue(feeLabel(price))
          }
        />
      </DetailList>

      {links.length > 0 && (
        <ActionList className="mt-3" items={links} onNavigate={onNavigate} />
      )}
      {calendar.modal}
    </Section>
  );
};

// ─────────────────────────────────────────────────────────────
//  Formulář přihlášky
// ─────────────────────────────────────────────────────────────

const FIELD_IDS = {
  weaponCaliber: "regWeaponCaliber",
  brake: "regBrake",
  shootingWith: "regShootingWith",
  weaponWith: "regWeaponWith",
  newbie: "regNewbie",
};

const extraQuestionId = (idx: number): string => `regExtra-${idx}`;

interface RegistrationFormProps {
  raceId: string;
  race: Race;
  profile: Profile;
  documentPrefill: DocumentPrefill | null;
  /** Startovné pro uživatele (calculatePrice) – ukládá se do registrace. */
  price: number;
  /** Ceník (defaults/categories) je načtený – do té doby se formulář nesmí odeslat. */
  pricingReady: boolean;
  /** Je přihlašování otevřené právě teď (čas se bere při odeslání, ne při mountu). */
  isOpenNow: () => boolean;
  onRegistered: (registration: Registration) => void;
  /** Při odeslání se zjistilo, že registrace už existuje. */
  onAlreadyRegistered: (registrations: Registration[]) => void;
  /** Transakce přihlášky zjistila plný závod (kapacita z aktuálních dat závodu). */
  onRaceFull: (capacity: number) => void;
  /**
   * Pravidla přihlášku odmítla (trvalé permission-denied, nebo nával
   * i po opakováních): rodič načte závod a profil znovu a zjistí důvod –
   * nové startovné (formulář pak ukazuje a příště odešle nové), neověřený
   * e-mail, uzávěrka podle času serveru (busy = nával – jen cena); null =
   * důvod se nenašel.
   */
  onRejected: (sentPrice: number, busy: boolean) => Promise<RegistrationRejection | null>;
  /**
   * Před zápisem přihlášky: token s ověřeným e-mailem (pravidla čtou
   * email_verified z tokenu). Nevyhazuje.
   */
  prepareSubmit: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Proč pravidla přihlášku odmítla (bezpečnost 2): startovné neodpovídá
 * aktuální ceně, e-mail není ověřený, uzávěrka podle času serveru už
 * proběhla (hodiny zařízení se mohou lišit).
 */
type RegistrationRejection =
  | { kind: "price"; price: number }
  | { kind: "unverified" }
  | { kind: "closed" };

/**
 * Jak blízko uzávěrky (podle hodin zařízení) se odmítnutí přihlášky
 * vysvětlí uzávěrkou – hodiny telefonu se od serveru mohou o pár minut
 * lišit.
 */
const CLOCK_SKEW_MS = 15 * 60 * 1000;

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  raceId,
  race,
  profile,
  documentPrefill,
  price,
  pricingReady,
  isOpenNow,
  onRegistered,
  onAlreadyRegistered,
  onRaceFull,
  onRejected,
  prepareSubmit,
  onCancel,
}) => {
  const { showToast } = useToast();
  const questions = race.extraQuestions;

  // Předvyplnění z profilu (uloží se zpět po přihlášení – jako dosud).
  const [weaponCaliber, setWeaponCaliber] = useState(profile.weaponCaliber);
  const [triggerBrake, setTriggerBrake] = useState(profile.triggerBrake || "ne");
  const [shootingWith, setShootingWith] = useState(profile.shootingWith);
  const [weaponWith, setWeaponWith] = useState(profile.weaponWith);
  // Trvale zakázaný nováček je vždy "ne" bez ohledu na uloženou hodnotu.
  const [isNewbie, setIsNewbie] = useState(
    profile.newbieDisabled ? "ne" : profile.isNewbie || "ne"
  );
  // Doklad totožnosti (otázky typu "document")
  const [docType, setDocType] = useState<DocTypeCode>(
    documentPrefill ? documentPrefill.type : "OP"
  );
  const [docNumber, setDocNumber] = useState(
    documentPrefill ? documentPrefill.number : ""
  );
  const [extraAnswers, setExtraAnswers] = useState<Record<string, string>>({});

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // ANTI-DUPLICITA vrstva 1: zámek proti více kliknutím / double-tapu.
  // useRef je synchronní – zabere i když React ještě nepřekreslil state.
  const submitLockRef = useRef(false);

  const errorOf = (id: string): string | undefined => errors[id] || undefined;
  const clearError = (id: string) =>
    setErrors((prev) => (prev[id] ? { ...prev, [id]: "" } : prev));

  const setAnswer = (label: string, value: string) =>
    setExtraAnswers((prev) => ({ ...prev, [label]: value }));

  /** Chyby per pole v pořadí formuláře (první klíč = první chybné pole). */
  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (!weaponCaliber.trim()) {
      next[FIELD_IDS.weaponCaliber] = "Zadejte ráži zbraně.";
    }
    questions.forEach((q, idx) => {
      const id = extraQuestionId(idx);
      if (q.type === "document") {
        if (!docNumber.trim()) {
          next[`${id}-number`] = `Zadejte číslo dokladu (${docTypeMeta(docType).short}).`;
        }
      } else if (q.required) {
        const answer = (extraAnswers[q.label] || "").trim();
        if (!answer) {
          next[id] =
            q.type === "yesno"
              ? "Vyberte Ano, nebo Ne."
              : "Odpovězte prosím na tuto otázku.";
        }
      }
    });
    return next;
  };

  /**
   * Vlastní zápis registrace – ANTI-DUPLICITA, tři vrstvy:
   *  1) submitLockRef … synchronní zámek proti double-tapu (v handleSubmit)
   *  2) deterministické ID dokumentu {raceId}__{uid} … druhý zápis nemá kam jít
   *  3) transakce createRegistrationCounted … atomické "read → if exists
   *     abort → create" a v téže transakci kapacita + obsazenost +1
   */
  const register = async (): Promise<void> => {
    // ── Odpovědi na doplňující otázky ──
    const finalAnswers: Record<string, string> = { ...extraAnswers };
    const trimmedDocNumber = docNumber.trim();
    let hasDocumentQuestion = false;
    for (const q of questions) {
      if (q.type === "document") {
        hasDocumentQuestion = true;
        // Do registrace (čte ji i správce a hlavní rozhodčí závodu)
        // NEUKLÁDÁME číslo dokladu – jen typ + příznak vyplnění. Číslo žije
        // v userDocuments (jen vlastník a správce).
        finalAnswers[q.label] = `${docTypeMeta(docType).short} (vyplněno)`;
      }
    }

    // ── Kontrola existující registrace – včetně historických záznamů
    //    s náhodným ID (query), ne jen deterministického dokumentu. ──
    const existing = await fetchOwnRegistrations(raceId, profile.uid);
    if (existing.length > 0) {
      onAlreadyRegistered(existing);
      showToast("Na tento závod už jste přihlášen.", "warning");
      return;
    }

    // ── Vrstva 2 + 3: deterministické ID + atomická transakce ──
    // Kapacitu hlídá transakce (aktuální počet i kapacita závodu) – dřívější
    // kontrola počtem přihlášek mimo transakci pustila souběžné přihlášky
    // přes kapacitu a cizí přihlášky už číst nejde.
    const regId = buildRegistrationId(raceId, profile.uid);
    // Zákaz nováčka mohl přibýt až po otevření formuláře (čerstvý profil po
    // odmítnuté přihlášce) – přepínač pak zmizí a odesílá se „ne".
    const newbie = profile.newbieDisabled ? "ne" : isNewbie;
    const registrationFields = {
      userId: profile.uid,
      raceId,
      firstName: profile.firstName,
      lastName: profile.lastName,
      email: profile.email,
      startFee: price,
      paidAmount: price === 0 ? price : 0,
      isPaid: price === 0,
      shootingWith: shootingWith || null,
      weaponWith: weaponWith || null,
      weaponCaliber,
      triggerBrake,
      extraAnswers: finalAnswers,
      isNewbie: newbie,
    };

    // Token s ověřeným e-mailem – e-mail mohl být ověřen až po vydání tokenu.
    await prepareSubmit();

    let result: CreateRegistrationResult;
    try {
      result = await createRegistrationCounted({
        raceId,
        regId,
        fields: registrationFields,
      });
    } catch (err) {
      if (err instanceof RaceFullError) {
        // Místo vzal někdo jiný (nebo správce snížil kapacitu) – formulář
        // vystřídá plný závod s čekací listinou.
        onRaceFull(err.capacity);
        showToast(
          "Závod je plně obsazen – přihlášení není možné. Můžete se zapsat na čekací listinu.",
          "warning"
        );
        return;
      }
      if (isPermissionDenied(err) || err instanceof RaceBusyError) {
        // Pravidla porovnávají startovné s cenou kategorie podle aktuálního
        // závodu a profilu; formulář ji spočítal při otevření stránky
        // (mezitím zaplacené členství, změna ceníku). Opakované odeslání by
        // poslalo zase starou cenu – formulář ji proto přepočítá. Při návalu
        // (počítadlo se pořád měnilo) mohla být důvodem odmítnutí taky cena.
        // Bezpečnost 2: pravidla chtějí i ověřený e-mail a uzávěrku podle
        // času serveru – rodič pak místo formuláře ukáže, co dělat. Nával
        // (RaceBusyError) se uzávěrkou ani ověřením nevysvětluje – u něj
        // jde jen o cenu, jinak platí hláška o návalu.
        const rejection = await onRejected(price, err instanceof RaceBusyError);
        if (rejection && rejection.kind === "price") {
          showToast(
            `Cena se mezitím změnila na ${formatCzk(rejection.price)} – zkontrolujte ji a odešlete znovu.`,
            "warning"
          );
          return;
        }
        if (rejection && rejection.kind === "unverified") {
          showToast("Před přihlášením na závod ověřte e-mail – postup najdete na stránce.", "warning");
          return;
        }
        if (rejection && rejection.kind === "closed") {
          showToast("Uzávěrka přihlášek už proběhla – přihlásit se už nejde.", "warning");
          return;
        }
      }
      throw err;
    }

    if (!result.created) {
      // Někdo (druhý tab, druhý tap, retry po timeoutu) to stihl první.
      onAlreadyRegistered([{ id: regId, data: result.existing }]);
      showToast("Na tento závod už jste přihlášen.", "warning");
      return;
    }

    // Registrace je uložená. Zbytek jsou doplňky – jejich selhání přihlášku
    // neruší (dřív se hlásilo „nepodařilo se", ačkoli registrace existovala).
    let extrasFailed = false;

    // Preference do profilu (předvyplnění příště)
    try {
      await updateDoc(doc(db, "users", profile.uid), {
        weaponCaliber,
        triggerBrake,
        shootingWith,
        weaponWith,
        isNewbie: newbie,
      });
    } catch (err) {
      extrasFailed = true;
      reportError("PaymentRace.saveUserPrefs", err, { uid: profile.uid });
    }

    // Číslo dokladu do PRIVÁTNÍ kolekce userDocuments/{uid} (čte jen vlastník
    // + admin – viz firestore.rules). Drží se jen pro daný rok závodu.
    if (hasDocumentQuestion && trimmedDocNumber) {
      try {
        await setDoc(doc(db, "userDocuments", profile.uid), {
          type: docType,
          number: trimmedDocNumber,
          year: raceYearOf(race),
          updatedAt: new Date(),
        });
      } catch (err) {
        extrasFailed = true;
        reportError("PaymentRace.saveDocument", err, { uid: profile.uid });
      }
    }

    onRegistered({ id: regId, data: registrationFields });
    if (extrasFailed) {
      showToast(
        "Přihláška je uložená, ale doplňující údaje (preference, číslo dokladu) se nepodařilo uložit. Dejte prosím vědět pořadateli.",
        "warning"
      );
    } else {
      showToast("Přihlášení proběhlo úspěšně.", "success");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitLockRef.current) return;

    const nextErrors = validate();
    setErrors(nextErrors);
    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      focusInvalid(firstInvalid);
      return;
    }

    // Tlačítko je do načtení ceníku neaktivní; pojistka pro Enter v poli.
    if (!pricingReady) return;

    if (!isOpenNow()) {
      showToast("Přihlašování je uzavřeno. Propozice zůstávají dostupné.", "warning");
      return;
    }

    // ── Vrstva 1: zámek ──
    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      await register();
    } catch (err) {
      if (err instanceof RaceBusyError) {
        // Nával: souběh o počítadlo prohraný i po opakováních. Nic se
        // nezapsalo a formulář zůstává – stačí odeslat znovu.
        reportWarning("PaymentRace.register.busy", err, { raceId });
        showToast(err.message, "warning");
      } else {
        reportError("PaymentRace.register", err, { raceId, uid: profile.uid });
        showToast(
          firebaseErrorMessage(err, "Přihlášení se nepodařilo. Zkuste to prosím znovu."),
          "error"
        );
      }
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Section
        title="Přihláška"
        icon="bi-pencil-square"
        description="Údaje o zbrani a preference se uloží i do profilu pro příští přihlášku."
      >
        <Field
          id={FIELD_IDS.weaponCaliber}
          label="Ráže zbraně"
          required
          error={errorOf(FIELD_IDS.weaponCaliber)}
        >
          <input
            type="text"
            className="form-control"
            value={weaponCaliber}
            maxLength={15}
            autoComplete="off"
            placeholder="např. .308 Win"
            onChange={(e) => {
              setWeaponCaliber(e.target.value);
              clearError(FIELD_IDS.weaponCaliber);
            }}
          />
        </Field>

        <YesNoToggle
          id={FIELD_IDS.brake}
          label="Kompenzátor / úsťová brzda"
          value={triggerBrake === "ano"}
          onChange={(v) => setTriggerBrake(v ? "ano" : "ne")}
        />

        <Field
          id={FIELD_IDS.shootingWith}
          label="Chci střílet v jedné rundě s"
          help="Volitelné – jméno střelce, se kterým chcete být v rundě."
        >
          <input
            type="text"
            className="form-control"
            value={shootingWith}
            maxLength={100}
            autoComplete="off"
            onChange={(e) => setShootingWith(e.target.value)}
          />
        </Field>

        <Field
          id={FIELD_IDS.weaponWith}
          label="Mám jednu zbraň s"
          help="Volitelné – jméno střelce, se kterým sdílíte zbraň."
        >
          <input
            type="text"
            className="form-control"
            value={weaponWith}
            maxLength={20}
            autoComplete="off"
            onChange={(e) => setWeaponWith(e.target.value)}
          />
        </Field>

        {!profile.newbieDisabled && (
          <YesNoToggle
            id={FIELD_IDS.newbie}
            label="Jste nováček?"
            description="Nováček střílí letošní sezónu poprvé. Pokud dvakrát zvítězí v této kategorii, bude přeřazen do běžné kategorie. Účast je dobrovolná."
            value={isNewbie === "ano"}
            onChange={(v) => setIsNewbie(v ? "ano" : "ne")}
          />
        )}

        {questions.length > 0 && (
          <>
            <SectionHeader
              title="Doplňující otázky pořadatele"
              icon="bi-question-circle"
            />
            {questions.map((q, idx) => {
              const id = extraQuestionId(idx);
              if (q.type === "yesno") {
                const answer = extraAnswers[q.label];
                return (
                  <YesNoToggle
                    key={id}
                    id={id}
                    label={q.label}
                    required={q.required}
                    value={answer === "ano" ? true : answer === "ne" ? false : null}
                    onChange={(v) => {
                      setAnswer(q.label, v ? "ano" : "ne");
                      clearError(id);
                    }}
                    error={errorOf(id)}
                  />
                );
              }
              if (q.type === "document") {
                return (
                  <React.Fragment key={id}>
                    <ChoiceGroup
                      id={`${id}-type`}
                      label={q.label || "Doklad totožnosti"}
                      required
                      options={DOC_TYPE_OPTIONS}
                      value={docType}
                      onChange={setDocType}
                    />
                    <Field
                      id={`${id}-number`}
                      label={docTypeMeta(docType).field}
                      required
                      help="Číslo vidí jen pořadatel a předvyplní se jen pro závody v tomto roce."
                      error={errorOf(`${id}-number`)}
                    >
                      <input
                        type="text"
                        className="form-control"
                        value={docNumber}
                        autoComplete="off"
                        onChange={(e) => {
                          setDocNumber(e.target.value);
                          clearError(`${id}-number`);
                        }}
                      />
                    </Field>
                  </React.Fragment>
                );
              }
              return (
                <Field
                  key={id}
                  id={id}
                  label={q.label}
                  required={q.required}
                  error={errorOf(id)}
                >
                  <input
                    type="text"
                    className="form-control"
                    value={extraAnswers[q.label] || ""}
                    placeholder="Vaše odpověď"
                    onChange={(e) => {
                      setAnswer(q.label, e.target.value);
                      clearError(id);
                    }}
                  />
                </Field>
              );
            })}
          </>
        )}
      </Section>

      {/* Přímý potomek <form>: sticky lišta nesmí být uvnitř Section */}
      <div className="lks-form-actions">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Zrušit
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting || !pricingReady}
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Přihlašuji…
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2" aria-hidden="true" />
              Přihlásit se
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// ─────────────────────────────────────────────────────────────
//  Platební pokyny – bankovní údaje se načítají až tady (jen když jsou potřeba)
// ─────────────────────────────────────────────────────────────

interface RacePaymentSectionProps {
  race: Race;
  raceDateObj: Date | null;
  /** Profil s platným členským číslem (bez něj rodič sekci nevykreslí). */
  profile: Profile;
  /** Částka k úhradě: startovné z registrace minus už zaplacená část. */
  amount: number;
  categoryLabel: string;
  /**
   * Živá cena se liší od startovného uloženého v registraci (změna kategorie
   * nebo ceníku po přihlášení) – pod pokyny se ukáže upozornění.
   */
  priceChanged: boolean;
}

const RacePaymentSection: React.FC<RacePaymentSectionProps> = ({
  race,
  raceDateObj,
  profile,
  amount,
  categoryLabel,
  priceChanged,
}) => {
  // Čte se při každém mountu (modul nic necachuje, jen sdílí souběžné
  // požadavky); chyba sítě projde jako status "error" s retry, null = klub
  // nemá nastavené ani číslo účtu, ani IBAN.
  const bank = useAsync(() => loadBankDetails(), []);

  if (bank.status === "ready" && bank.data) {
    const variableSymbol = buildRaceVariableSymbol(race, profile.sequenceNumber);
    const message = racePaymentMessage(profile.lastName, race.raceNumber);
    // QR (SPAYD) i e-mail s QR kódem jdou sestavit jen s IBAN; bez něj
    // platí číslo účtu + VS a PaymentInstructions to tak i vykreslí.
    const qrData =
      bank.data.iban !== ""
        ? buildPaymentQrData({
            iban: bank.data.iban,
            amount,
            message,
            variableSymbol,
          })
        : null;
    // Při změně kategorie/ceníku od přihlášení by e-mail nesl živou kategorii
    // k uložené částce – bez vysvětlení; e-mail se proto nenabízí.
    const onSendEmail =
      qrData === null || priceChanged
        ? undefined
        : () =>
            sendRacePaymentEmail({
              raceName: race.name,
              raceDate: raceDateObj ?? new Date(race.date),
              shootingRange: race.shootingRange,
              mapUrl: race.mapUrl,
              firstName: profile.firstName,
              lastName: profile.lastName,
              categoryLabel,
              fee: amount,
              variableSymbol,
              email: profile.email,
              qrData,
              message,
            });
    // Řádek Splatnost jen dokud termín neprošel – prošlé datum by jen mátlo
    // (e-mail si svůj termín počítá sám, beze změny).
    const dueDate = raceDateObj ? paymentDueDate(raceDateObj) : null;
    const dueDateShown =
      dueDate && dueDate.getTime() > Date.now() ? dueDate : undefined;

    return (
      <PaymentInstructions
        amount={amount}
        variableSymbol={variableSymbol}
        message={message}
        bankDetails={bank.data}
        dueDate={dueDateShown}
        qrData={qrData ?? undefined}
        recipientNote={`${variableSymbol} ${profile.lastName}`}
        emailSentLabel={`Odesláno na ${profile.email}`}
        onSendEmail={onSendEmail}
      >
        {priceChanged ? (
          <p className="small text-warning-emphasis mt-3 mb-0">
            <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
            Vaše kategorie nebo ceník se od přihlášení změnily – částku ověřte
            u pořadatele. E-mail s platebními údaji proto není k dispozici.
          </p>
        ) : null}
        <p className="small text-muted mt-3 mb-0">
          Po spárování platby se stav přihlášky změní na „Zaplaceno".
        </p>
      </PaymentInstructions>
    );
  }

  return (
    <Section title="Platební údaje" icon="bi-bank">
      <AsyncState
        status={bank.status}
        error={bank.error}
        onRetry={bank.reload}
        loadingText="Načítám platební údaje…"
      >
        <EmptyState
          icon="bi-bank"
          title="Bankovní údaje nejsou nastavené"
          text="Správce klubu zatím nezadal číslo účtu. Zkuste to prosím později."
        />
      </AsyncState>
    </Section>
  );
};

// ─────────────────────────────────────────────────────────────
//  Sekce „Vaše přihláška" – uložené odpovědi + odhlášení
// ─────────────────────────────────────────────────────────────

interface RegistrationDetailsProps {
  registration: Registration;
  busy: boolean;
  onUnregister: () => void;
}

const RegistrationDetailsSection: React.FC<RegistrationDetailsProps> = ({
  registration,
  busy,
  onUnregister,
}) => {
  const d = registration.data;
  const rows: { label: string; value: string }[] = [];
  const push = (label: string, value: unknown) => {
    const text = typeof value === "string" ? value.trim() : "";
    if (text) rows.push({ label, value: text });
  };
  push("Ráže zbraně", d.weaponCaliber);
  push("Kompenzátor / úsťová brzda", yesNoLabel(d.triggerBrake));
  push("Chci střílet v jedné rundě s", d.shootingWith);
  push("Mám jednu zbraň s", d.weaponWith);
  push("Nováček", yesNoLabel(d.isNewbie));
  const answers = d.extraAnswers;
  if (answers && typeof answers === "object") {
    Object.keys(answers).forEach((label) => {
      const raw = (answers as Record<string, unknown>)[label];
      push(label || "Doklad", yesNoLabel(raw));
    });
  }

  return (
    <Section title="Vaše přihláška" icon="bi-person-check">
      {rows.length > 0 ? (
        <DetailList>
          {rows.map((row) => (
            <DetailRow key={row.label} label={row.label} value={row.value} />
          ))}
        </DetailList>
      ) : (
        <p className="text-muted mb-0">Podrobnosti přihlášky nejsou k dispozici.</p>
      )}
      <div className="d-grid mt-3">
        <button
          type="button"
          className="btn btn-outline-danger"
          disabled={busy}
          onClick={onUnregister}
        >
          {busy ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Odhlašuji…
            </>
          ) : (
            <>
              <i className="bi bi-x-circle me-2" aria-hidden="true" />
              Odhlásit ze závodu
            </>
          )}
        </button>
      </div>
    </Section>
  );
};

// ─────────────────────────────────────────────────────────────
//  Sekce „Čekací listina" – plný závod (upozornění v aplikaci a e-mail);
//  u závodu, kterému správce kapacitu zrušil, jen odebrání z listiny
// ─────────────────────────────────────────────────────────────

interface WaitlistSectionProps {
  /** Pořadí střelce na listině; null = na listině není. */
  position: { position: number; total: number } | null;
  /** Počet zapsaných; null = listina se nenačetla. */
  total: number | null;
  /** Místo se uvolnilo (střelec je na listině a nad sekcí je formulář). */
  spotFreed: boolean;
  /**
   * Závod už kapacitu nemá (správce ji zrušil) – pořadí nic neznamená,
   * sekce slouží jen k odebrání z listiny.
   */
  unlimited?: boolean;
  busy: boolean;
  onJoin: () => void;
  onLeave: () => void;
}

const WaitlistSection: React.FC<WaitlistSectionProps> = ({
  position,
  total,
  spotFreed,
  unlimited = false,
  busy,
  onJoin,
  onLeave,
}) => (
  <Section
    title="Čekací listina"
    icon="bi-hourglass-split"
    description={
      unlimited
        ? "Závod už nemá omezenou kapacitu – přihláška vás z čekací listiny odebere automaticky."
        : spotFreed
        ? "Místo dostane, kdo se přihlásí dřív – přihláška vás z čekací listiny odebere automaticky."
        : "Když se místo uvolní, uvidíte upozornění tady i na přehledu závodů a pošleme vám e-mail (může přijít se zpožděním – rychlejší je upozornění v aplikaci). Místo dostane, kdo se přihlásí první – pořadí na listině je orientační, ostatní na listině zůstávají."
    }
  >
    {position && unlimited ? (
      <p className="mb-0">Jste na čekací listině.</p>
    ) : position ? (
      <p className="mb-0">
        Jste na čekací listině –{" "}
        <strong>
          {position.position}. z {position.total}
        </strong>
      </p>
    ) : total !== null ? (
      <p className="mb-0 text-muted">
        {total > 0
          ? `Na čekací listině je zapsáno: ${total}.`
          : "Na čekací listině zatím nikdo není."}
      </p>
    ) : null}
    <div className={position || total !== null ? "d-grid mt-3" : "d-grid"}>
      <button
        type="button"
        className={position ? "btn btn-outline-danger" : "btn btn-primary"}
        disabled={busy}
        onClick={position ? onLeave : onJoin}
      >
        {busy ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
            {position ? "Odebírám…" : "Zapisuji…"}
          </>
        ) : position ? (
          <>
            <i className="bi bi-x-circle me-2" aria-hidden="true" />
            Odebrat z čekací listiny
          </>
        ) : (
          <>
            <i className="bi bi-hourglass-split me-2" aria-hidden="true" />
            Zapsat se na čekací listinu
          </>
        )}
      </button>
    </div>
  </Section>
);

// ─────────────────────────────────────────────────────────────
//  Obrazovka po načtení dat
// ─────────────────────────────────────────────────────────────

interface RaceRegistrationScreenProps {
  raceId: string;
  /** Závod z načtení stránky (odmítnutá přihláška ho načte znovu). */
  initialRace: Race;
  /** Profil z načtení stránky (odmítnutá přihláška ho načte znovu). */
  initialProfile: Profile;
  initialRegistrations: Registration[];
  /** Obsazenost z race_stats; null = neznámá (závod bez kapacity, chyba čtení). */
  initialCount: number | null;
  /** Čekací listina závodu; null = nenačtená (přihlášený střelec, chyba čtení). */
  initialWaitlist: WaitlistEntry[] | null;
  documentPrefill: DocumentPrefill | null;
  /** Přihlášený účet (ověření e-mailu – bezpečnost 2). */
  user: User | null;
  /** Po odhlášení znovu načte celou stránku (čerstvý profil, předvyplnění, obsazenost). */
  onUnregistered: () => void;
}

const RaceRegistrationScreen: React.FC<RaceRegistrationScreenProps> = ({
  raceId,
  initialRace,
  initialProfile,
  initialRegistrations,
  initialCount,
  initialWaitlist,
  documentPrefill,
  user,
  onUnregistered,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  // Přihláška jen s ověřeným e-mailem (pravidla, bezpečnost 2) – neověřený
  // účet (relace ze starší verze aplikace) uvidí místo formuláře hlášku
  // s novým odesláním ověřovacího e-mailu.
  const emailCheck = useEmailVerification(user);
  // Uzávěrka podle času serveru (odmítnutá přihláška těsně u uzávěrky) –
  // hodiny zařízení mohou jít pozadu.
  const [closedByServer, setClosedByServer] = useState(false);
  // Závod a profil, ze kterých se počítá cena a kategorie – po odmítnuté
  // přihlášce je nahradí čerstvé ze serveru (diagnoseRejection).
  const [race, setRace] = useState<Race>(initialRace);
  const [profile, setProfile] = useState<Profile>(initialProfile);
  // Věková hranice juniora (defaults/categories): bez ní nejde určit cenu
  // ani kategorii – formulář se do té doby nedá odeslat. Chyba čtení
  // (bez hodnoty v cache) se ukáže v řádcích kategorie/startovné s retry.
  const {
    juniorMaxAge,
    ready: pricingLoaded,
    error: pricingError,
    reload: reloadPricing,
  } = useJuniorMaxAge();
  const pricingReady = pricingLoaded && pricingError == null;

  const [registration, setRegistration] = useState<Registration | null>(
    initialRegistrations.length > 0 ? initialRegistrations[0] : null
  );
  // Kapacita z načtení stránky; transakce přihlášky ji může zpřesnit
  // (správce ji mezitím změnil – RaceFullError nese aktuální hodnotu).
  const [capacity, setCapacity] = useState<number | null>(race.maxCapacity);
  const [registrationCount, setRegistrationCount] = useState<number | null>(
    initialCount
  );
  const [waitlist, setWaitlist] = useState<WaitlistEntry[] | null>(
    initialWaitlist
  );
  const [isUnregistering, setIsUnregistering] = useState(false);
  const [waitlistBusy, setWaitlistBusy] = useState(false);

  const raceDateObj = useMemo(() => toDateSafe(race.date), [race]);

  // Uzávěrka: z DB, jinak začátek dne závodu v Praze (jako pravidla).
  const registrationClosesAt = useMemo(() => registrationClosesAtOf(race), [race]);

  // Vyhodnocuje se při každém vykreslení i při odeslání (ne jednou při mountu).
  const isOpenNow = () =>
    !closedByServer && !!registrationClosesAt && new Date() < registrationClosesAt;
  const canRegister = isOpenNow();

  const priceUser = useMemo(() => priceUserOf(profile), [profile]);
  const price = calculatePrice(race, priceUser, juniorMaxAge);
  const categoryLabel = priceCategoryLabel(
    getPriceCategory(race, priceUser, juniorMaxAge)
  );

  const isLksRace = race.organizedByLKS !== false;
  // Neznámý počet (chyba čtení) = neplno – formulář zůstává, kapacitu
  // pohlídá transakce přihlášky.
  const isFull = isRaceFull(registrationCount, capacity);

  // Čekací listina (varianta C): nepřihlášený střelec a otevřené přihlašování.
  // Zapsat se jde jen na plný závod s kapacitou. Pořadí je orientační – místo
  // dostane, kdo se přihlásí dřív; přihláška záznam na listině smaže v téže
  // transakci. Kdo na listině je, vidí ji (a může se odebrat) i u závodu,
  // kterému správce kapacitu zrušil – jinak by upozornění „Uvolnilo se
  // místo" na přehledu svítilo až do uzávěrky.
  const waitlistActive = canRegister && !registration;
  const waitlistOpen = waitlistActive && capacity !== null;
  const myWaitlist =
    waitlistActive && waitlist ? waitlistPosition(waitlist, profile.uid) : null;
  // Uvolněné místo: střelec je na listině a známý počet je pod kapacitou
  // (závod bez kapacity = volno).
  const spotFreed =
    myWaitlist !== null &&
    (capacity === null || (!isFull && registrationCount !== null));

  // Stav zaplacení z dat registrace (paidAmount ≥ startFee) – stejně jako dashboard.
  const paid = registration ? isRegistrationPaid(registration.data) : false;

  // Bez platného členského čísla nejde sestavit variabilní symbol (VS =
  // YY + RRR + SEQ); přihlásit se střelec může, platební údaje ne.
  const hasMemberNumber = isValidSequenceNumber(profile.sequenceNumber);

  // Částka u existující registrace jde z jejího dokumentu (startFee minus
  // už zaplacená část), ne ze živé ceny – ta se od přihlášení mohla změnit
  // (ceník, kategorie). Živá cena platí jen před přihlášením. Historický
  // záznam bez startFee je podle isRegistrationPaid „zaplacený" (fee 0),
  // takže k úhradě není nic – žádná záloha na živou cenu.
  const storedFee = registration ? Number(registration.data.startFee) : NaN;
  const hasStoredFee = Number.isFinite(storedFee);
  const paidSoFar = registration
    ? Number(registration.data.paidAmount) || 0
    : 0;
  const due = hasStoredFee ? Math.max(0, storedFee - paidSoFar) : 0;
  const priceChanged = hasStoredFee && pricingReady && price !== storedFee;

  /** Lokální listina bez vlastního záznamu (odebrání, přihláška). */
  const dropOwnWaitlistEntry = () =>
    setWaitlist((list) =>
      list ? list.filter((e) => e.userId !== profile.uid) : list
    );

  /**
   * Dočte obsazenost a čekací listinu (best-effort – chyba čtení nechá
   * dosavadní stav). Vrací čerstvé hodnoty, null = nepodařilo se.
   */
  const refreshAvailability = async (): Promise<{
    count: number | null;
    list: WaitlistEntry[] | null;
  }> => {
    const [count, list] = await Promise.all([
      loadRegisteredCountSafe(raceId),
      loadRaceWaitlistSafe(raceId),
    ]);
    if (count !== null) setRegistrationCount(count);
    if (list !== null) setWaitlist(list);
    return { count, list };
  };

  const handleRegistered = (reg: Registration) => {
    setRegistration(reg);
    setRegistrationCount((n) => (n === null ? n : n + 1));
    // Záznam na čekací listině smazala transakce přihlášky.
    dropOwnWaitlistEntry();
    // Formulář zmizí a nahoře je stav + platební údaje – ať je uživatel vidí.
    scrollMainToTop();
  };

  const handleAlreadyRegistered = (regs: Registration[]) => {
    setRegistration(regs[0]);
    scrollMainToTop();
  };

  /**
   * Přihláška odmítnutá pravidly i po opakování (RegistrationForm.onRejected):
   * startovné se kontroluje proti aktuálnímu závodu a profilu, formulář ho
   * ale spočítal při otevření stránky (mezitím zaplacené členství, změna
   * ceníku). Načte obojí ze serveru – stránka pak ukazuje a formulář příště
   * odešle aktuální cenu – a vrátí ji, když se od odeslané liší.
   * Bezpečnost 2: pravidla chtějí i ověřený e-mail (stav účtu ze serveru –
   * neověřený uvidí místo formuláře hlášku) a uzávěrku podle času serveru
   * (aktuální uzávěrka závodu; hodiny zařízení se mohou o pár minut lišit,
   * proto CLOCK_SKEW_MS – formulář pak vystřídá „Přihlašování je
   * uzavřeno“). null = důvod se nenašel nebo se čtení nepovedlo (platí
   * původní chyba).
   */
  const diagnoseRejection = async (
    sentPrice: number,
    busy: boolean
  ): Promise<RegistrationRejection | null> => {
    let freshRace: Race;
    let freshProfile: Profile;
    try {
      const [raceSnap, userSnap] = await Promise.all([
        getDocFromServer(doc(db, "races", raceId)),
        getDocFromServer(doc(db, "users", profile.uid)),
      ]);
      if (!raceSnap.exists() || !userSnap.exists()) return null;
      freshRace = toRace(raceSnap.id, raceSnap.data());
      freshProfile = toProfile(profile.uid, userSnap.data());
    } catch (err) {
      reportWarning("PaymentRace.diagnoseRejection", err, { raceId });
      return null;
    }
    setRace(freshRace);
    setProfile(freshProfile);
    if (freshRace.maxCapacity !== capacity) {
      setCapacity(freshRace.maxCapacity);
      refreshAvailability();
    }
    const freshPrice = calculatePrice(
      freshRace,
      priceUserOf(freshProfile),
      juniorMaxAge
    );
    if (freshPrice !== sentPrice) return { kind: "price", price: freshPrice };
    // Nával: důvodem byl souběh o počítadlo, ne ověření ani uzávěrka.
    if (busy) return null;
    // Zákaz nováčka mezitím přibyl – formulář přepínač skryl a příště
    // odešle „ne“; dosavadní chyba, uzávěrkou se to nevysvětluje.
    if (freshProfile.newbieDisabled && !profile.newbieDisabled) return null;

    let verified = true;
    try {
      verified = await emailCheck.recheck();
    } catch (err) {
      reportWarning("PaymentRace.recheckEmail", err, { raceId });
    }
    if (!verified) return { kind: "unverified" };

    const closes = registrationClosesAtOf(freshRace);
    if (!closes || Date.now() >= closes.getTime() - CLOCK_SKEW_MS) {
      setClosedByServer(true);
      return { kind: "closed" };
    }
    return null;
  };

  /** Token s ověřeným e-mailem před zápisem přihlášky (best-effort). */
  const prepareSubmit = async (): Promise<void> => {
    if (!user || !user.emailVerified) return;
    try {
      await ensureVerifiedToken(user);
    } catch (err) {
      reportWarning("PaymentRace.verifiedToken", err, { raceId });
    }
  };

  // Plno zjištěné při odeslání: hned „plno" (počet ≥ kapacita), přesné číslo
  // a aktuální čekací listinu dočte refreshAvailability.
  const handleRaceFull = (cap: number) => {
    setCapacity(cap);
    setRegistrationCount((n) => (n !== null && n >= cap ? n : cap));
    refreshAvailability();
  };

  const handleJoinWaitlist = async () => {
    if (waitlistBusy) return;
    setWaitlistBusy(true);
    let failure: unknown = null;
    try {
      await joinWaitlist(raceId, profile.uid);
      // Hned na listině (pořadí na konci); přesné pořadí dočte refresh níže.
      setWaitlist((list) => [
        ...(list || []).filter((e) => e.userId !== profile.uid),
        {
          id: waitlistId(raceId, profile.uid),
          raceId,
          userId: profile.uid,
          createdAt: null,
        },
      ]);
    } catch (err) {
      failure = err;
      reportError("PaymentRace.joinWaitlist", err, { raceId, uid: profile.uid });
    }
    // Pravidla zápis odmítnou i tehdy, když se mezitím uvolnilo místo nebo
    // záznam už existuje (druhý tab – přepsat ho nejde); čerstvá data to
    // rozliší (a ukážou formulář nebo pořadí).
    const fresh = await refreshAvailability();
    setWaitlistBusy(false);
    const onList =
      fresh.list !== null && waitlistPosition(fresh.list, profile.uid) !== null;
    if (failure === null || onList) {
      showToast(
        "Zapsali jsme vás na čekací listinu. Když se místo uvolní, uvidíte upozornění v aplikaci a pošleme vám e-mail (ve dne do 15 minut, v noci do hodiny).",
        "success"
      );
    } else if (fresh.count !== null && !isRaceFull(fresh.count, capacity)) {
      showToast("Mezitím se uvolnilo místo – můžete se přihlásit.", "info");
    } else {
      showToast(
        firebaseErrorMessage(
          failure,
          "Zápis na čekací listinu se nepodařil. Zkuste to prosím znovu."
        ),
        "error"
      );
    }
  };

  const handleLeaveWaitlist = async () => {
    if (waitlistBusy) return;
    const ok = await confirm({
      title: "Odebrat z čekací listiny?",
      message:
        capacity === null
          ? "Závod už nemá omezenou kapacitu – upozornění na uvolněné místo zmizí, přihlásit se můžete i tak."
          : myWaitlist
          ? `Přijdete o své pořadí (${myWaitlist.position}. z ${myWaitlist.total}). Když se zapíšete znovu, zařadíte se na konec.`
          : "Přijdete o své pořadí. Když se zapíšete znovu, zařadíte se na konec.",
      tone: "danger",
      confirmLabel: "Odebrat",
    });
    if (!ok) return;
    setWaitlistBusy(true);
    try {
      await leaveWaitlist(raceId, profile.uid);
      dropOwnWaitlistEntry();
      showToast("Odebrali jsme vás z čekací listiny.", "info");
    } catch (err) {
      reportError("PaymentRace.leaveWaitlist", err, { raceId, uid: profile.uid });
      showToast(
        firebaseErrorMessage(
          err,
          "Odebrání z čekací listiny se nepodařilo. Zkuste to prosím znovu."
        ),
        "error"
      );
    } finally {
      setWaitlistBusy(false);
    }
  };

  // Odhlášení – smaže VŠECHNY registrace uživatele na tento závod
  // (self-healing případných historických duplicit) i řádek startovní listiny.
  const handleUnregister = async () => {
    if (!registration || isUnregistering) return;

    const fee = Number(registration.data.startFee) || 0;
    const rawPaid = registration.data.paidAmount;
    const hasPaidAmount = rawPaid !== undefined && rawPaid !== null;
    const paidAmount = Number(rawPaid) || 0;
    const impacts: React.ReactNode[] = [];
    if (paidAmount > 0) {
      impacts.push(
        <li key="paid">
          Máte zaplaceno {formatCzk(paidAmount)}
          {paidAmount !== fee ? ` (startovné ${formatCzk(fee)})` : ""} – o
          vrácení napište na <ContactLink />.
        </li>
      );
    } else if (!hasPaidAmount && registration.data.isPaid === true && fee > 0) {
      // Historický záznam jen s příznakem isPaid (bez paidAmount).
      impacts.push(
        <li key="paid">
          Startovné {formatCzk(fee)} už máte zaplacené – o jeho vrácení se
          domluvte s pořadatelem.
        </li>
      );
    }
    if (!canRegister) {
      impacts.push(
        <li key="closed">
          Uzávěrka přihlášek už proběhla – znovu se přihlásit nepůjde.
        </li>
      );
    }

    const ok = await confirm({
      title: "Odhlásit ze závodu?",
      message: (
        <>
          Odhlásíte se ze závodu <strong>{race.name}</strong>. Přihláška
          i místo ve startovní listině se smažou.
        </>
      ),
      details:
        impacts.length > 0 ? <ul className="mb-0 ps-3">{impacts}</ul> : undefined,
      tone: "danger",
      confirmLabel: "Odhlásit",
    });
    if (!ok) return;

    setIsUnregistering(true);
    let done = false;
    let deleted = 0;
    try {
      // Aktuální stav z DB (mohla vzniknout duplicita jinde). Maže se jen to,
      // co opravdu existuje: neexistující přihlášku s náhodným id pravidla
      // nedovolí ani přečíst (transakce by spadla).
      const current = await fetchOwnRegistrations(raceId, profile.uid);

      // Po jedné – každé smazání sníží obsazenost o 1 v téže transakci
      // (pravidla neumí −N v jedné dávce; historické duplicity).
      for (const reg of current) {
        if (await deleteRegistrationCounted(reg.id)) deleted += 1;
      }

      // Úklid řádku veřejné startovní listiny – pravidla ho střelci dovolí
      // smazat až bez přihlášky raceId__uid, proto samostatně po ní.
      try {
        await deleteDoc(doc(db, startListPath(raceId), profile.uid));
      } catch (err) {
        reportWarning("PaymentRace.unregister.startlist", err, { raceId });
      }

      setRegistration(null);
      setRegistrationCount((n) => (n === null ? n : Math.max(0, n - deleted)));
      done = true;
    } catch (err) {
      if (err instanceof RaceBusyError) {
        // Nával: odhlášení prohrálo souběh o počítadlo i po opakováních –
        // přihláška zůstala, stačí to zkusit znovu.
        reportWarning("PaymentRace.unregister.busy", err, { raceId, deleted });
        showToast(err.message, "warning");
      } else {
        reportError("PaymentRace.unregister", err, { raceId, uid: profile.uid });
        showToast(
          firebaseErrorMessage(err, "Odhlášení se nepodařilo. Zkuste to prosím znovu."),
          "error"
        );
      }
    } finally {
      setIsUnregistering(false);
    }
    if (done) {
      showToast("Odhlášení proběhlo úspěšně.", "info");
    }
    if (done || deleted > 0) {
      // Celá stránka znovu (profil, předvyplnění dokladu, obsazenost) – lokální
      // stav nahoře je jen do té doby, než rodič tuhle obrazovku přemountuje.
      // I po chybě, když se část přihlášek (duplicity) už smazala.
      onUnregistered();
    }
  };

  // ── Banner stavu (jen jeden) ──
  // Přihlášený: cizí závod má přednost (LKS platbu neeviduje, „čeká na
  // platbu" by byl nesmysl), pak zaplaceno, pak čeká na platbu.
  // Nepřihlášený: uvolněné místo pro čekací listinu (nad formulářem), plno
  // před uzávěrkou – stejně jako karta na dashboardu.
  let status: React.ReactNode = null;
  if (registration) {
    const fee = Number(registration.data.startFee) || 0;
    if (!isLksRace) {
      status = (
        <InfoCard
          icon="bi-info-circle-fill"
          tone="info"
          title="Přihlášen"
          subtitle="Závod nepořádá LKS – startovné plaťte podle pokynů pořadatele."
          className="mb-3"
        />
      );
    } else if (paid) {
      status = (
        <InfoCard
          icon="bi-check-circle-fill"
          tone="success"
          title="Zaplaceno"
          subtitle={
            fee > 0
              ? "Na závod jste přihlášen a startovné je uhrazené."
              : hasStoredFee
              ? "Na závod jste přihlášen, závod je bez startovného."
              : "Na závod jste přihlášen; startovné u této přihlášky není evidováno – v případě pochybností ověřte u pořadatele."
          }
          className="mb-3"
        />
      );
    } else {
      status = (
        <InfoCard
          icon="bi-hourglass-split"
          tone="warning"
          title={
            due > 0
              ? `Přihlášen – čeká na platbu ${formatCzk(due)}`
              : "Přihlášen – čeká na platbu"
          }
          subtitle={
            !hasMemberNumber
              ? "Platební údaje nejde sestavit – u vašeho účtu chybí členské číslo (podrobnosti níže)."
              : paidSoFar > 0
              ? `Zaplaceno ${formatCzk(paidSoFar)} z ${formatCzk(storedFee)}. Platební údaje najdete níže.`
              : due > 0
              ? "Platební údaje najdete níže."
              : "Částku k úhradě upřesní pořadatel."
          }
          className="mb-3"
        />
      );
    }
  } else if (spotFreed) {
    status = (
      <InfoCard
        icon="bi-bell-fill"
        tone="success"
        title="Uvolnilo se místo – můžete se přihlásit"
        subtitle={
          capacity === null
            ? "Jste na čekací listině a závod už nemá omezenou kapacitu – přihláška je níže. Když jet nechcete, odeberte se z čekací listiny."
            : "Jste na čekací listině. Místo dostane, kdo se přihlásí dřív – přihláška je níže."
        }
        className="mb-3"
      />
    );
  } else if (isFull) {
    status = (
      <InfoCard
        icon="bi-people-fill"
        tone="danger"
        title="Závod je plně obsazen"
        subtitle={
          !waitlistOpen
            ? `Kapacita ${capacity} míst je naplněná.`
            : myWaitlist
            ? `Kapacita ${capacity} míst je naplněná – jste na čekací listině.`
            : `Kapacita ${capacity} míst je naplněná – můžete se zapsat na čekací listinu.`
        }
        className="mb-3"
      />
    );
  } else if (!canRegister) {
    status = (
      <InfoCard
        icon="bi-lock-fill"
        tone="dark"
        title="Přihlašování je uzavřeno"
        subtitle={
          // Uzavřeno podle času serveru, hodiny zařízení ještě ne (jdou pozadu).
          registrationClosesAt && closedByServer && new Date() < registrationClosesAt
            ? `Uzávěrka ${formatCzDateTime(registrationClosesAt)} podle času serveru už proběhla (hodiny zařízení se mírně liší). Propozice i mapa zůstávají dostupné.`
            : registrationClosesAt
            ? `Uzávěrka byla ${formatCzDateTime(registrationClosesAt)}. Propozice i mapa zůstávají dostupné.`
            : "Závod nemá platné datum. Propozice i mapa zůstávají dostupné."
        }
        className="mb-3"
      />
    );
  }

  return (
    <>
      {status}

      <RaceSummarySection
        race={race}
        raceDateObj={raceDateObj}
        registrationClosesAt={registrationClosesAt}
        capacity={capacity}
        registrationCount={registrationCount}
        isFull={isFull}
        isLksRace={isLksRace}
        pricingReady={pricingReady}
        pricingError={pricingError}
        onRetryPricing={reloadPricing}
        categoryLabel={categoryLabel}
        price={price}
        registeredFee={registration && hasStoredFee ? storedFee : undefined}
        onNavigate={navigate}
      />

      {registration ? (
        <>
          {!paid && isLksRace && due > 0 ? (
            hasMemberNumber ? (
              <RacePaymentSection
                race={race}
                raceDateObj={raceDateObj}
                profile={profile}
                amount={due}
                categoryLabel={categoryLabel}
                priceChanged={priceChanged}
              />
            ) : (
              <InfoCard
                icon="bi-exclamation-triangle"
                tone="warning"
                title="Chybí vaše členské číslo"
                subtitle={
                  <>
                    Bez něj nejde sestavit variabilní symbol. Obraťte se na
                    správce klubu (<ContactLink />).
                  </>
                }
                className="mb-3"
              />
            )
          ) : null}
          <RegistrationDetailsSection
            registration={registration}
            busy={isUnregistering}
            onUnregister={handleUnregister}
          />
          <div className="d-grid">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate("/user")}
            >
              <i className="bi bi-arrow-left me-2" aria-hidden="true" />
              Zpět na přehled
            </button>
          </div>
        </>
      ) : canRegister && !isFull ? (
        <>
          {emailCheck.verified ? (
            <RegistrationForm
              raceId={raceId}
              race={race}
              profile={profile}
              documentPrefill={documentPrefill}
              price={price}
              pricingReady={pricingReady}
              isOpenNow={isOpenNow}
              onRegistered={handleRegistered}
              onAlreadyRegistered={handleAlreadyRegistered}
              onRaceFull={handleRaceFull}
              onRejected={diagnoseRejection}
              prepareSubmit={prepareSubmit}
              onCancel={() => navigate("/user")}
            />
          ) : (
            <EmailVerificationNotice state={emailCheck} variant="race" className="mb-3" />
          )}
          {/* Na listině, ale místo je volné (nebo závod už kapacitu nemá):
              odebrání až za formulářem */}
          {myWaitlist ? (
            <WaitlistSection
              position={myWaitlist}
              total={waitlist ? waitlist.length : null}
              spotFreed={spotFreed}
              unlimited={capacity === null}
              busy={waitlistBusy}
              onJoin={handleJoinWaitlist}
              onLeave={handleLeaveWaitlist}
            />
          ) : null}
        </>
      ) : waitlistOpen ? (
        <WaitlistSection
          position={myWaitlist}
          total={waitlist ? waitlist.length : null}
          spotFreed={false}
          busy={waitlistBusy}
          onJoin={handleJoinWaitlist}
          onLeave={handleLeaveWaitlist}
        />
      ) : null}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
//  Stránka: načtení dat + stavy (načítám / chyba s retry / nenalezeno)
// ─────────────────────────────────────────────────────────────

const PaymentRace: React.FC = () => {
  const { raceId } = useParams<{ raceId: string }>();
  const navigate = useNavigate();
  // Nepřihlášeného přesměruje RequireAuth – uid je tu vždy, guard je pojistka.
  const { user } = useAuth();
  const uid = user ? user.uid : null;

  const page = useAsync<PageData | null>(
    () => (raceId && uid ? loadPage(raceId, uid) : Promise.resolve(null)),
    [raceId, uid]
  );

  const goHome = () => navigate("/user");

  return (
    <AsyncState
      status={page.status}
      error={page.error}
      onRetry={page.reload}
      loadingText="Načítám přihlášku…"
    >
      {!page.data || !raceId ? (
        <LoadingState text="Načítám přihlášku…" />
      ) : !page.data.race ? (
        <EmptyState
          icon="bi-calendar-x"
          title="Závod nenalezen"
          text="Závod byl zrušen nebo odkaz už neplatí."
          action={{ label: "Zpět na přehled", onClick: goHome }}
        />
      ) : !page.data.profile ? (
        // Registraci nebo nedokončené smazání účtu dokončí přihlašovací
        // stránka (AuthPage); stejný text jako Profil, Úprava profilu
        // a Členství (nález 6 3. kola). Zpět vede šipka v hlavičce.
        <EmptyState
          icon="bi-person-x"
          title="Profil nenalezen"
          text={`K účtu chybí údaje člena – registrace se nedokončila. Dokončete ji tlačítkem níže (je k tomu potřeba připojení k internetu). Pokud jste účet mazali, dokončíte tam i smazání. Když to nepůjde, napište nám na ${CONTACT_EMAIL}.`}
          action={{ label: "Dokončit registraci", onClick: () => navigate("/login") }}
        />
      ) : (
        <RaceRegistrationScreen
          raceId={raceId}
          initialRace={page.data.race}
          initialProfile={page.data.profile}
          initialRegistrations={page.data.registrations}
          initialCount={page.data.registrationCount}
          initialWaitlist={page.data.waitlist}
          documentPrefill={page.data.documentPrefill}
          user={user}
          onUnregistered={page.reload}
        />
      )}
    </AsyncState>
  );
};

export default PaymentRace;
